import { useIsOfficeAdmin } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Card, Page } from "@rentiohq/web-shared/dist/components";
import { TabAction } from "@rentiohq/web-shared/dist/types";
import { Outlet, matchPath, useLocation } from "react-router-dom";
import { ESettingsPaths } from "./Settings.types";

const getFullSettingsPath = (subpath: ESettingsPaths) =>
  `/${ESettingsPaths.Base}/${subpath}`;

const tenantProfilePath = getFullSettingsPath(ESettingsPaths.TenantProfile);
const rentmatchScorePath = getFullSettingsPath(ESettingsPaths.RentmatchScore);
const tenantProfileNotificationsPath = getFullSettingsPath(
  ESettingsPaths.TenantProfileNotifications,
);
const templatePreferencesPath = getFullSettingsPath(
  ESettingsPaths.TemplatePreferences,
);
const communicationPath = getFullSettingsPath(ESettingsPaths.Communication);
const checklistPreferencesPath = getFullSettingsPath(
  ESettingsPaths.ChecklistPreferences,
);
const latePaymentPenaltyPreferencesPath = getFullSettingsPath(
  ESettingsPaths.LatePaymentPenaltyPreferences,
);

export const Settings = () => {
  const tabs: TabAction[] = [];

  let location = useLocation();
  const isOfficeAdmin = useIsOfficeAdmin();
  const hasLatePaymentPenaltyForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.LatePaymentPenalty,
  );

  // Not useBrokerFeature: that reports "no feature" while the office record is
  // still on its way, and on a page opened straight from the address bar it is
  // always still on its way. The tab would vanish on every refresh.
  const { isFetchingBroker, hasFeature: hasRentMatch } =
    brokerHooks.useBrokerFeatureLoader(EBrokerFeature.RentMatch);

  tabs.push({
    name: ESettingsPaths.TenantProfile,
    content: getLocalizedText("page.settings.tab.mytenantprofile"),
    url: tenantProfilePath,
    isActive: !!matchPath(tenantProfilePath, location.pathname),
  });

  // What the office scores candidates on, which is not a setting of the tenant
  // profile. Only an office with the feature has anything to score.
  if (isFetchingBroker || hasRentMatch) {
    tabs.push({
      name: ESettingsPaths.RentmatchScore,
      content: getLocalizedText("page.settings.tab.rentmatch_score"),
      url: rentmatchScorePath,
      isActive: !!matchPath(rentmatchScorePath, location.pathname),
    });
  }

  tabs.push({
    name: ESettingsPaths.TenantProfileNotifications,
    content: getLocalizedText("page.settings.tab.mailing_preferences"),
    url: tenantProfileNotificationsPath,
    isActive: !!matchPath(tenantProfileNotificationsPath, location.pathname),
  });

  tabs.push({
    name: ESettingsPaths.TemplatePreferences,
    content: getLocalizedText("page.settings.tab.template_preferences"),
    url: templatePreferencesPath,
    isActive: !!matchPath(templatePreferencesPath, location.pathname),
  });

  tabs.push({
    name: ESettingsPaths.Communication,
    content: getLocalizedText("page.settings.tab.communication"),
    url: communicationPath,
    isActive: !!matchPath(communicationPath, location.pathname),
  });

  tabs.push({
    name: ESettingsPaths.ChecklistPreferences,
    content: getLocalizedText("page.settings.tab.checklist_preferences"),
    url: checklistPreferencesPath,
    isActive: !!matchPath(checklistPreferencesPath, location.pathname),
  });

  // Only office admins may change the late payment penalty settings, and the API rejects
  // anyone else, so employees never get to see a form they cannot save. The office needs
  // the late payment penalty feature as well.
  if (isOfficeAdmin && hasLatePaymentPenaltyForBroker) {
    tabs.push({
      name: ESettingsPaths.LatePaymentPenaltyPreferences,
      content: getLocalizedText("page.settings.tab.late_payment_penalty"),
      url: latePaymentPenaltyPreferencesPath,
      isActive: !!matchPath(
        latePaymentPenaltyPreferencesPath,
        location.pathname,
      ),
    });
  }

  return (
    <Page title={getLocalizedText("page.settings.title")} tabs={tabs}>
      <Card>
        <Outlet />
      </Card>
    </Page>
  );
};
